<template>
    <div
        v-if="abas.length > 1"
        class="doc-tabs"
    >
        <button
            v-for="aba in abas"
            :key="aba.nome"
            :class="['doc-tab', {active: aba.nome==abaAtual}]"
            @click="abaAtual=aba.nome"
        >
            {{ aba.titulo }}
        </button>
    </div>

    <div class="doc-dynamic">
        <template v-for="bloco in blocosRaiz" :key="bloco.key">
            <DocGroupBlock
                v-if="bloco.tipo === 'GROUP'"
                :grupo="bloco.item"
                :itens="filhosPorPai(bloco.item.id)"
                @update:item="updateItem"
            />

            <DocItemTable
                v-else-if="bloco.tipo === 'TABLE'"
                :table="bloco.item"
                :itens="filhosPorPai(bloco.item.id)"
                :disabled="disabled"
                @update:item="updateItem"
                @add-row="tableAddRow"
                @delete-row="tableDeleteRow"
            />

            <DocSessionRow
                v-else-if="bloco.tipo === 'SESSION'"
                :item="bloco.item"
            />

            <div
                v-else-if="bloco.tipo === 'ROW'"
                class="row row-space"
            >
                <component
                    v-for="item in bloco.itens"
                    :key="item.id"
                    :is="getComponent(item)"
                    :item="item"
                    @update:item="updateItem"
                />
            </div>
        </template>
    </div>
</template>

<script setup>
import { computed, ref, watchEffect } from 'vue';
import { Api } from '@/funcoes/apiExport.js';
import { globalStore } from '@/globals.js';

import DocGroupBlock from './DocGroupBlock.vue';
import DocItemTable from './DocItemTable.vue';
import DocSessionRow from './DocSessionRow.vue';

import DocItemField from './DocItemField.vue';
import DocItemMemo from './DocItemMemo.vue';
import DocItemSelect from './DocItemSelect.vue';
import DocItemCheck from './DocItemCheck.vue';
import DocItemUnsupported from './DocItemUnsupported.vue';
import DocRadioGroupRow from './DocRadioGroupRow.vue';

const abaAtual = ref('');
const isLoading = ref(false);
const msgAlerta = ref(null);

const props = defineProps({
    itens: {
        type: Array,
        default: () => []
    },
    disabled: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:itens', 'tableAddRow', 'tableDeleteRow']);


const abas = computed(() => {
    const lista = [];

    for (const item of itensOrdenados.value) {
        const nome = item.cdaba?.trim() || '1.DADOS';

        if (!lista.find(a => a.nome === nome)) {
            lista.push({
                nome,
                titulo: nome
            });
        }
    }

    return lista.sort((a, b) => a.nome.localeCompare(b.nome, undefined, {
        numeric: true
    }));
})


const componentRegistry = {
    FIELD: DocItemField,
    MEMO: DocItemMemo,
    SELECT: DocItemSelect,
    CHECK: DocItemCheck,
    RADIO_GROUP: DocRadioGroupRow
}


const itensAba = computed(() => {
    return itensOrdenados.value.filter(i =>
        (i.cdaba?.trim() || '1.DADOS') === abaAtual.value
    );
})


const itensOrdenados = computed(() => {
    return [...props.itens].sort((a, b) => {
        const ordemA = Number(a.ordem ?? 0);
        const ordemB = Number(b.ordem ?? 0);

        if (ordemA !== ordemB) return ordemA - ordemB;

        const linhaA = Number(a.nrtable_linha || 0);
        const linhaB = Number(b.nrtable_linha || 0);

        if (linhaA !== linhaB) return linhaA - linhaB;

        return Number(a.id ?? 0) - Number(b.id ?? 0);
    });
});

const blocosRaiz = computed(() => {
    const blocos = [];
    const rows = new Map();

    itensAba.value
        .filter(item => !item.idpai)
        .forEach(item => {
            if (item.tpitem === 'GROUP') {
                blocos.push({
                    key: `GROUP-${item.id}`,
                    tipo: 'GROUP',
                    item,
                    ordem: Number(item.ordem ?? 0)
                });

                return;
            }

            if (item.tpitem === 'TABLE') {
                blocos.push({
                    key: `TABLE-${item.id}`,
                    tipo: 'TABLE',
                    item,
                    ordem: Number(item.ordem ?? 0)
                });

                return;
            }

            if (item.tpitem === 'SESSION') {
                blocos.push({
                    key: `SESSION-${item.id}`,
                    tipo: 'SESSION',
                    item,
                    ordem: Number(item.ordem ?? 0)
                });

                return;
            }

            const cdlinha = item.cdlinha ?? item.ordem ?? item.id;
            const rowKey = `ROW-${cdlinha}`;

            if (!rows.has(rowKey)) {
                const row = {
                    key: rowKey,
                    tipo: 'ROW',
                    cdlinha,
                    ordem: Number(item.ordem ?? 0),
                    itens: []
                };

                rows.set(rowKey, row);
                blocos.push(row);
            }

            rows.get(rowKey).itens.push(item);
        });

    blocos.forEach(bloco => {
        if (bloco.tipo === 'ROW') {
            bloco.itens.sort((a, b) => Number(a.ordem ?? 0) - Number(b.ordem ?? 0));
        }
    });

    return blocos.sort((a, b) => a.ordem - b.ordem);
})


function filhosPorPai(idpai) {
    return itensOrdenados.value.filter(item => Number(item.idpai) === Number(idpai));
}

function getComponent(item) {
    return componentRegistry[item.tpresposta] || DocItemUnsupported;
}


async function tableAddRow(payload){
    const itemTable = payload.table;
    try{
        isLoading.value=true;
        const response = await Api.genericPost('/cad/exec_requisicao', {
            nmrequisicao: 'spdoc_novo_registro',
            intparam1: itemTable.iddoc,
            intparam2: itemTable.id,
            idlogin: globalStore.idlogin});
        if (response.bolok === 1){
            const rLinha = await Api.genericPost('/lsdoc/doc/itens', {
                idreferencia: itemTable.iddoc,
                intparam1: itemTable.id,
                intparam2: response.id
            });

            const novosItens = rLinha || [];

            emit('update:itens', [
                ...props.itens,
                ...novosItens
            ]);
        }else{
            msgAlerta.value = response.erro;
        }
    } catch (error){msgAlerta.value= error;
    } finally {isLoading.value = false;}
}


async function tableDeleteRow(itemTable){
    try{
        isLoading.value=true;
        const response = await Api.genericPost('/cad/exec_requisicao', {
            nmrequisicao: 'doc_item_delete_row',
            intparam1: itemTable.iddoc,
            intparam2: itemTable.idpai,
            idreferencia: itemTable.nrtable_linha,
            idlogin: globalStore.idlogin});
        if (response.bolok === 1){
            const novaLista = props.itens.filter(item =>
                !(
                    Number(item.iddoc) === Number(itemTable.iddoc) &&
                    Number(item.idpai) === Number(itemTable.idpai) &&
                    Number(item.nrtable_linha) === Number(itemTable.nrtable_linha)
                )
            );

            emit('update:itens', novaLista);
        }else{
            msgAlerta.value = response.erro;
        }
    } catch (error){msgAlerta.value= error;
    } finally {isLoading.value = false;}
}


function updateItem(itemAtualizado) {
    const novaLista = props.itens.map(item => {
        if (Number(item.id) === Number(itemAtualizado.id)) {
            return itemAtualizado;
        }

        return item;
    });

    emit('update:itens', novaLista);
}


watchEffect(() => {
    if (!abas.value.length) {
        abaAtual.value = '';
        return;
    }

    const existeAbaAtual = abas.value.some(a => a.nome === abaAtual.value);

    if (!existeAbaAtual) {
        abaAtual.value = abas.value[0].nome;
    }
});
</script>

<style scoped>
.doc-dynamic {
    margin-top: 2px;
}
.doc-tabs{
    display:flex;
    gap:4px;
    margin-bottom:12px;
    border-bottom:1px solid #ddd;
}

.doc-tab{
    padding:8px 16px;
    background:#f6f6f6;
    color:#555;
    border:1px solid transparent;
    border-radius:6px 6px 0 0;
    cursor:pointer;
    transition:all .15s ease;
}

.doc-tab:hover:not(.active){
    background:#e9ecef;
    color:#222;
    border-color:#d5d5d5;
}

.doc-tab.active{
    background:#fff;
    color:#000;
    font-weight:600;
    border:1px solid #ddd;
    border-bottom-color:#fff;
    position:relative;
    z-index:2;
}
</style>
